import * as Sentry from '@sentry/browser';

const SENSITIVE_QUERY_KEYS = new Set([
  'access_token',
  'assertion',
  'auth',
  'authorization',
  'client_secret',
  'code',
  'id_token',
  'password',
  'refresh_token',
  'secret',
  'session',
  'token',
]);

const SENSITIVE_HEADER_KEYS = new Set([
  'authorization',
  'cookie',
  'proxy-authorization',
  'set-cookie',
  'x-api-key',
  'x-auth-token',
  'x-csrf-token',
  'x-xsrf-token',
]);

const redactUrl = (url: string | undefined): string | undefined => {
  if (!url) {
    return url;
  }

  try {
    const origin =
      typeof window === 'undefined'
        ? 'http://localhost'
        : window.location.origin;
    const parsed = new URL(url, origin);
    for (const key of [...parsed.searchParams.keys()]) {
      if (SENSITIVE_QUERY_KEYS.has(key.toLowerCase())) {
        parsed.searchParams.set(key, '[Filtered]');
      }
    }
    return parsed.toString();
  } catch {
    return url;
  }
};

type QueryString =
  | string
  | Record<string, string>
  | Array<[string, string]>
  | undefined;

const redactQueryString = (queryString: QueryString): QueryString => {
  if (typeof queryString === 'string') {
    const prefix = queryString.startsWith('?') ? '?' : '';
    const rawQuery = prefix ? queryString.slice(1) : queryString;
    const params = new URLSearchParams(rawQuery);
    for (const key of [...params.keys()]) {
      if (SENSITIVE_QUERY_KEYS.has(key.toLowerCase())) {
        params.set(key, '[Filtered]');
      }
    }
    return `${prefix}${params.toString()}`;
  }

  if (Array.isArray(queryString)) {
    return queryString.map(([key, value]): [string, string] =>
      SENSITIVE_QUERY_KEYS.has(String(key).toLowerCase())
        ? [key, '[Filtered]']
        : [key, value]
    );
  }

  if (queryString && typeof queryString === 'object') {
    return Object.fromEntries(
      Object.entries(queryString).map(([key, value]) => [
        key,
        SENSITIVE_QUERY_KEYS.has(key.toLowerCase()) ? '[Filtered]' : value,
      ])
    );
  }

  return queryString;
};

const stripHeaders = (
  headers: Record<string, string> | undefined
): Record<string, string> | undefined => {
  if (!headers) {
    return headers;
  }

  return Object.fromEntries(
    Object.entries(headers).map(([key, value]) => [
      key,
      SENSITIVE_HEADER_KEYS.has(key.toLowerCase()) ? '[Filtered]' : value,
    ])
  );
};

const scrubRequest = (
  request: NonNullable<Sentry.Event['request']>
): Sentry.Event['request'] => {
  const nextRequest = { ...request };
  delete nextRequest.cookies;
  delete nextRequest.data;
  delete nextRequest.env;
  nextRequest.headers = stripHeaders(
    nextRequest.headers as Record<string, string> | undefined
  );
  nextRequest.query_string = redactQueryString(
    nextRequest.query_string as QueryString
  ) as typeof nextRequest.query_string;
  nextRequest.url = redactUrl(nextRequest.url);
  return nextRequest;
};

const scrubUser = (
  user: NonNullable<Sentry.Event['user']>
): Sentry.Event['user'] => {
  const nextUser = { ...user };
  delete nextUser.email;
  delete nextUser.geo;
  delete nextUser.id;
  delete nextUser.ip_address;
  delete nextUser.username;
  return nextUser;
};

const scrubBreadcrumbs = (
  breadcrumbs: NonNullable<Sentry.Event['breadcrumbs']>
): Sentry.Event['breadcrumbs'] =>
  breadcrumbs.map((crumb) => {
    if (!crumb.data || typeof crumb.data.url !== 'string') {
      return crumb;
    }
    return {
      ...crumb,
      data: {
        ...crumb.data,
        url: redactUrl(crumb.data.url),
      },
    };
  });

export const scrubSentryEvent = <T extends Sentry.Event>(event: T): T => ({
  ...event,
  request: event.request ? scrubRequest(event.request) : event.request,
  user: event.user ? scrubUser(event.user) : event.user,
  breadcrumbs: event.breadcrumbs
    ? scrubBreadcrumbs(event.breadcrumbs)
    : event.breadcrumbs,
});

export const initSentry = (): void => {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) {
    return;
  }

  Sentry.init({
    dsn,
    sendDefaultPii: false,
    beforeSend: (event) => scrubSentryEvent(event),
    beforeSendTransaction: (event) => scrubSentryEvent(event),
  });
};
